import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { api } from "../lib/api";
import { tokenStore } from "../lib/tokenStore";
import { userWantsPersistentAlerts } from "../lib/orderAlert";
import { FULL_SCREEN_EVENTS, isFullScreenEvent } from "../lib/notificationRules";
import BigToggle from "./BigToggle";

/**
 * The signed-in admin's own switch for persistent order alerts (dialog + repeating sound for new and cancelled
 * orders). Saved on their user account only; pushes keep arriving when it's off.
 */
export default function PersistentAlertSetting() {
    const { user, refresh } = useAuth() || {};
    const { tenant } = useTenant() || {};
    const [busy, setBusy] = useState(false);
    // Available once ANY event is set to full screen — by a tenant rule (Admin -> Notifications) or, for a
    // tenant that saved none, by the legacy Super-Admin alert config.
    const available = [...FULL_SCREEN_EVENTS].some((e) => isFullScreenEvent(tenant, e));
    const on = userWantsPersistentAlerts(user);
    // "View as admin" signs in as a shared service account (or someone else's), not the admin's own login.
    let impersonating = !!user?.is_service;
    try { impersonating = impersonating || tokenStore.isImpersonation(); } catch { /* storage blocked */ }

    const toggle = async () => {
        setBusy(true);
        try {
            await api.patch("/auth/me/notification-preferences", { persistent_order_alerts: !on });
            await refresh?.();
            toast.success(!on ? "Loud order alerts turned on" : "Loud order alerts turned off");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save the setting");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-3 mb-2 rounded-xl border border-border px-3 py-2.5" data-testid="persistent-alert-setting">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold leading-tight">Enable Loud/Persistent Order Alerts</p>
                <BigToggle testid="persistent-alert-toggle" on={on} disabled={busy || !available || impersonating} onClick={toggle} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1" data-testid="persistent-alert-note">
                {!available
                    ? "No event is set to a full-screen alert yet — set one under Notifications."
                    : impersonating
                        ? "You're viewing as admin. Each admin changes this from their own login."
                        : "Full-screen alert with a repeating sound for new and cancelled orders. Only affects your account; notifications still arrive when off."}
            </p>
        </div>
    );
}
